<template>
  <div
    class="accordion-image-gallery"
    :class="{
      'accordion-image-gallery--with-list-markers': showListMarkers,
      'accordion-image-gallery--en': isEnglish
    }"
    :aria-label="ariaLabel"
    :style="galleryStyle"
  >
    <div class="accordion-image-gallery__list">
      <div
        v-for="item in items"
        :key="item.key"
        class="accordion-image-gallery__card"
        :class="{
          'accordion-image-gallery__card--active': currentKey === item.key
        }"
      >
        <button
          class="accordion-image-gallery__trigger"
          :class="{
            'accordion-image-gallery__trigger--active': currentKey === item.key
          }"
          type="button"
          :aria-expanded="currentKey === item.key"
          :aria-controls="`accordion-image-gallery-panel-${item.key}`"
          @pointerenter="scheduleActivate(item.key)"
          @pointerleave="cancelHoverActivation"
          @click="activateKey(item.key)"
        >
          <div class="accordion-image-gallery__card-head">
            <div class="accordion-image-gallery__card-copy">
              <div v-if="item.initial" class="accordion-image-gallery__initial">{{ item.initial }}</div>
              <div class="accordion-image-gallery__heading">
                <h3
                  class="accordion-image-gallery__title"
                  :class="{ 'accordion-image-gallery__title--hidden': !isTitleVisible(item) }"
                >{{ item.title }}</h3>
                <p
                  v-if="previewFor(item)"
                  class="accordion-image-gallery__summary"
                  :class="{ 'accordion-image-gallery__summary--hidden': isTitleVisible(item) }"
                >{{ previewFor(item) }}</p>
              </div>
              <div class="accordion-image-gallery__detail-wrap">
                <div
                  v-if="contentFor(item).length"
                  :id="`accordion-image-gallery-panel-${item.key}`"
                  class="accordion-image-gallery__detail"
                >
                  <ul v-if="isListBody(item)">
                    <li v-for="subtitle in contentFor(item)" :key="subtitle">{{ subtitle }}</li>
                  </ul>
                  <p v-else class="accordion-image-gallery__body">
                    <span
                      v-for="segment in detailSegmentsFor(item)"
                      :key="`${item.key}-${segment.index}`"
                      :class="{
                        'accordion-image-gallery__body-highlight': segment.highlighted,
                        'accordion-image-gallery__body-nowrap': segment.noWrap
                      }"
                    >
                      {{ segment.text }}
                    </span>
                  </p>
                </div>
              </div>
            </div>
            <span
              class="accordion-image-gallery__chevron"
              :class="{
                'accordion-image-gallery__chevron--active': currentKey === item.key
              }"
              aria-hidden="true"
            >
              <span class="accordion-image-gallery__chevron-icon"></span>
            </span>
          </div>
        </button>

      </div>
    </div>

    <figure class="accordion-image-gallery__media">
      <transition name="accordion-image-gallery__image">
        <img
          :key="currentKey"
          class="accordion-image-gallery__image"
          :src="activeItem?.image"
          :alt="activeItem?.imageAlt || activeItem?.title || ''"
          :style="{ objectPosition: activeItem?.imagePosition || '' }"
        />
      </transition>
    </figure>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'

export interface AccordionImageGalleryItem {
  key: string
  title: string
  summary?: string
  description?: string
  detail?: string | string[]
  body?: string | string[]
  subtitles?: string[]
  detailHighlights?: string[]
  image: string
  imageAlt?: string
  imagePosition?: string
  initial?: string
}

interface TextSegment {
  text: string
  highlighted: boolean
  index: number
  noWrap?: boolean
}

const props = withDefaults(defineProps<{
  items: AccordionImageGalleryItem[]
  ariaLabel?: string
  defaultActiveKey?: string
  showListMarkers?: boolean
}>(), {
  ariaLabel: '',
  defaultActiveKey: '',
  showListMarkers: false
})

const getFirstKey = () => props.items[0]?.key || ''

const activeKey = ref(props.defaultActiveKey || getFirstKey())
const { locale } = useI18n()
const isEnglish = computed(() => locale.value === 'en')

const activeItem = computed(() => {
  return props.items.find((item) => item.key === activeKey.value) || props.items[0]
})

const currentKey = computed(() => {
  return props.items.some((item) => item.key === activeKey.value) ? activeKey.value : getFirstKey()
})

const galleryStyle = computed(() => ({
  '--accordion-item-count': String(Math.max(props.items.length, 1)),
  '--accordion-row-tracks': props.items.length
    ? props.items
      .map((item) => item.key === currentKey.value ? 'var(--accordion-card-expanded-height)' : 'var(--accordion-card-collapsed-height)')
      .join(' ')
    : '1fr'
}))

const previewFor = (item: AccordionImageGalleryItem) => {
  return item.summary?.trim() || ''
}

const isTitleVisible = (item: AccordionImageGalleryItem) => {
  return currentKey.value === item.key || !previewFor(item)
}

const contentFor = (item: AccordionImageGalleryItem) => {
  if (Array.isArray(item.detail)) return item.detail
  if (typeof item.detail === 'string' && item.detail.trim()) return [item.detail]
  if (Array.isArray(item.body)) return item.body
  if (typeof item.body === 'string' && item.body.trim()) return [item.body]
  if (item.subtitles?.length) return item.subtitles
  if (typeof item.description === 'string' && item.description.trim()) return [item.description]
  if (typeof item.summary === 'string' && item.summary.trim()) return [item.summary]
  return []
}

const isListBody = (item: AccordionImageGalleryItem) => {
  return contentFor(item).length > 1
}

const splitNoWrapSegments = (text: string, highlighted: boolean, startIndex: number): TextSegment[] => {
  const pattern = new RegExp(
    '(\\d+(?:\\.\\d+)?(?:\\s?(?:[\\u4E00-\\u9FFF]+|[A-Za-z%]+|cm|mm|km|kg|Hz|GB|MB|TB|\\u2103|\\u2109|\\u00B0))+)',
    'g'
  )
  const segments: TextSegment[] = []
  let cursor = 0
  let segmentIndex = startIndex
  let match: RegExpExecArray | null

  while ((match = pattern.exec(text)) !== null) {
    const matchText = match[0]
    const matchIndex = match.index

    if (matchIndex > cursor) {
      segments.push({
        text: text.slice(cursor, matchIndex),
        highlighted,
        index: segmentIndex++
      })
    }

    segments.push({
      text: matchText,
      highlighted,
      index: segmentIndex++,
      noWrap: true
    })

    cursor = matchIndex + matchText.length
  }

  if (cursor < text.length) {
    segments.push({
      text: text.slice(cursor),
      highlighted,
      index: segmentIndex++
    })
  }

  return segments.length ? segments : [{ text, highlighted, index: startIndex }]
}

const detailSegmentsFor = (item: AccordionImageGalleryItem): TextSegment[] => {
  const text = contentFor(item)[0] || ''
  const highlights = item.detailHighlights?.map((highlight) => highlight.trim()).filter(Boolean) || []

  if (!text || !highlights.length) {
    return splitNoWrapSegments(text, false, 0)
  }

  const segments: TextSegment[] = []
  let cursor = 0
  let segmentIndex = 0

  while (cursor < text.length) {
    let nextIndex = -1
    let nextHighlight = ''

    highlights.forEach((highlight) => {
      const matchIndex = text.indexOf(highlight, cursor)
      if (matchIndex !== -1 && (nextIndex === -1 || matchIndex < nextIndex)) {
        nextIndex = matchIndex
        nextHighlight = highlight
      }
    })

    if (nextIndex === -1 || !nextHighlight) {
      segments.push(...splitNoWrapSegments(text.slice(cursor), false, segmentIndex))
      break
    }

    if (nextIndex > cursor) {
      const plainSegments = splitNoWrapSegments(text.slice(cursor, nextIndex), false, segmentIndex)
      segments.push(...plainSegments)
      segmentIndex = plainSegments[plainSegments.length - 1].index + 1
    }

    const highlightSegments = splitNoWrapSegments(nextHighlight, true, segmentIndex)
    segments.push(...highlightSegments)
    segmentIndex = highlightSegments[highlightSegments.length - 1].index + 1
    cursor = nextIndex + nextHighlight.length
  }

  return segments
}

let hoverActivationTimer: ReturnType<typeof window.setTimeout> | null = null

const cancelHoverActivation = () => {
  if (hoverActivationTimer !== null) {
    window.clearTimeout(hoverActivationTimer)
    hoverActivationTimer = null
  }
}

const activateKey = (key: string) => {
  cancelHoverActivation()
  if (key !== activeKey.value) {
    activeKey.value = key
  }
}

const scheduleActivate = (key: string) => {
  cancelHoverActivation()
  hoverActivationTimer = window.setTimeout(() => {
    activateKey(key)
  }, 60)
}

onBeforeUnmount(() => {
  cancelHoverActivation()
})
</script>

<style scoped>
.accordion-image-gallery {
  width: min(100%, 1200px);
  margin: 0 auto;
  padding: 0 var(--accordion-shell-padding, var(--pixso-gap-md));
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: var(--accordion-grid-gap, var(--pixso-gap-md));
  align-items: stretch;
  --accordion-gap: var(--pixso-gap-sm);
  --accordion-gallery-height: 710px;
  --accordion-card-collapsed-height: 120px;
  --accordion-card-expanded-height: calc((var(--accordion-gallery-height) - (var(--accordion-item-count) - 1) * var(--accordion-gap) - (var(--accordion-item-count) - 1) * var(--accordion-card-collapsed-height)));
  --accordion-card-radius: calc(var(--pixso-radius-lg) - var(--pixso-gap-sm));
  --accordion-media-radius: var(--pixso-radius-lg);
  --accordion-trigger-padding-block: var(--pixso-fs-16);
  --accordion-trigger-padding-inline: calc(var(--pixso-gap-lg) - var(--pixso-gap-xs) * 2);
  --accordion-copy-gap: calc(var(--pixso-gap-sm) - var(--pixso-gap-xs));
  --accordion-collapsed-copy-gap: var(--pixso-gap-xs);
  --accordion-detail-offset: var(--pixso-gap-sm);
  --accordion-list-padding: var(--pixso-gap-md);
  --accordion-ease: cubic-bezier(0.16, 1, 0.3, 1);
  --accordion-card-duration: 400ms;
  --accordion-content-duration: 300ms;
}

.accordion-image-gallery__list {
  display: grid;
  grid-template-rows: var(--accordion-row-tracks);
  gap: var(--accordion-gap);
  height: var(--accordion-gallery-height);
  transition: grid-template-rows var(--accordion-card-duration) var(--accordion-ease);
}

.accordion-image-gallery__card {
  border-radius: var(--accordion-card-radius);
  background: var(--pixso-color-surface);
  color: var(--pixso-color-text);
  text-align: left;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  transition: background-color 180ms ease, transform var(--accordion-card-duration) var(--accordion-ease);
  will-change: transform;
  transform: translateZ(0);
}

.accordion-image-gallery__trigger {
  width: 100%;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: inherit;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  box-sizing: border-box;
  min-height: 100%;
  height: 100%;
  cursor: pointer;
  font: inherit;
  padding: var(--accordion-trigger-padding-block) var(--accordion-trigger-padding-inline);
}

.accordion-image-gallery__trigger--active {
  align-items: stretch;
  justify-content: center;
}

.accordion-image-gallery__card-head {
  display: flex;
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
  gap: 15px;
}

.accordion-image-gallery__card:not(.accordion-image-gallery__card--active) .accordion-image-gallery__trigger {
  justify-content: center;
}

.accordion-image-gallery__card:not(.accordion-image-gallery__card--active) .accordion-image-gallery__card-head {
  align-items: center;
}

.accordion-image-gallery__card-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--accordion-copy-gap);
  transition: gap var(--accordion-content-duration) var(--accordion-ease);
}

.accordion-image-gallery__initial {
  color: var(--pixso-color-muted);
  font-family: var(--pixso-font-cn);
  font-weight: 700;
  font-size: var(--pixso-fs-48);
  line-height: var(--pixso-lh-48);
  transition: color var(--accordion-content-duration) var(--accordion-ease), font-size var(--accordion-content-duration) var(--accordion-ease), line-height var(--accordion-content-duration) var(--accordion-ease);
}

.accordion-image-gallery__title {
  margin: 0;
  font-family: var(--pixso-font-cn);
  font-weight: 700;
  font-size: var(--pixso-fs-16);
  line-height: var(--pixso-lh-16);
  color: var(--pixso-color-muted);
  transition: color var(--accordion-content-duration) var(--accordion-ease), font-size var(--accordion-content-duration) var(--accordion-ease), line-height var(--accordion-content-duration) var(--accordion-ease), opacity var(--accordion-content-duration) var(--accordion-ease);
}

.accordion-image-gallery__summary {
  margin: 0;
  color: var(--pixso-color-muted);
  font-family: var(--pixso-font-cn);
  font-weight: 700;
  font-size: var(--pixso-fs-16);
  line-height: var(--pixso-lh-16);
  transition: opacity var(--accordion-content-duration) var(--accordion-ease), color var(--accordion-content-duration) var(--accordion-ease);
}

.accordion-image-gallery__heading {
  display: grid;
}

.accordion-image-gallery__heading .accordion-image-gallery__title,
.accordion-image-gallery__heading .accordion-image-gallery__summary {
  grid-area: 1 / 1;
}

.accordion-image-gallery__title--hidden,
.accordion-image-gallery__summary--hidden {
  opacity: 0;
}

.accordion-image-gallery__chevron {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.accordion-image-gallery__chevron-icon {
  width: 24px;
  height: 24px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100% 100%;
  background-image: url('@/assets/images/pixso-police-solution/accordion-chevron-down.png');
}

.accordion-image-gallery__chevron--active .accordion-image-gallery__chevron-icon {
  background-image: url('@/assets/images/pixso-police-solution/accordion-chevron-up.png');
}

.accordion-image-gallery__detail {
  padding: var(--accordion-detail-offset) 0 0;
  color: var(--pixso-color-muted);
  font-family: var(--pixso-font-cn);
  font-weight: 700;
  font-size: var(--pixso-fs-16);
  line-height: var(--pixso-lh-16);
  overflow: hidden;
  min-height: 0;
  opacity: 1;
  transition: opacity var(--accordion-content-duration) var(--accordion-ease);
}

.accordion-image-gallery__detail-wrap {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows var(--accordion-card-duration) var(--accordion-ease);
}

.accordion-image-gallery__card--active .accordion-image-gallery__detail-wrap {
  grid-template-rows: 1fr;
}

.accordion-image-gallery__card:not(.accordion-image-gallery__card--active) .accordion-image-gallery__detail {
  opacity: 0;
}

.accordion-image-gallery__body {
  margin: 0;
}

.accordion-image-gallery__body-nowrap {
  white-space: nowrap;
}

.accordion-image-gallery__body-highlight {
  color: #fff;
}

.accordion-image-gallery__detail ul {
  margin: 0;
  padding-left: 0;
  list-style: none;
}

.accordion-image-gallery--with-list-markers .accordion-image-gallery__detail ul {
  list-style: disc;
  padding-left: calc(var(--accordion-list-padding) + var(--pixso-gap-xs));
}

.accordion-image-gallery--with-list-markers .accordion-image-gallery__detail li::marker {
  color: currentColor;
  font-size: var(--pixso-fs-10);
}

.accordion-image-gallery__detail li + li {
  margin-top: var(--accordion-copy-gap);
}

.accordion-image-gallery__media {
  position: relative;
  margin: 0;
  border-radius: var(--accordion-media-radius);
  overflow: hidden;
  background: var(--pixso-color-surface);
  height: var(--accordion-gallery-height);
  transform: translateZ(0);
  isolation: isolate;
}

.accordion-image-gallery__image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.accordion-image-gallery__card--active .accordion-image-gallery__initial {
  color: var(--pixso-color-primary);
  font-size: var(--pixso-fs-64);
  line-height: var(--pixso-lh-64);
}

.accordion-image-gallery__card--active .accordion-image-gallery__title {
  font-size: var(--pixso-fs-20);
  line-height: var(--pixso-lh-20);
  color: var(--pixso-color-primary);
}

.accordion-image-gallery--en .accordion-image-gallery__initial {
  font-family: var(--pixso-font-en);
  font-size: var(--pixso-fs-36);
  line-height: var(--pixso-lh-36);
}

.accordion-image-gallery--en .accordion-image-gallery__title,
.accordion-image-gallery--en .accordion-image-gallery__summary,
.accordion-image-gallery--en .accordion-image-gallery__detail {
  font-family: var(--pixso-font-en);
  font-size: var(--pixso-fs-14);
  line-height: var(--pixso-lh-14);
}

.accordion-image-gallery--en .accordion-image-gallery__card--active .accordion-image-gallery__initial {
  font-size: var(--pixso-fs-56);
  line-height: var(--pixso-lh-56);
}

.accordion-image-gallery--en .accordion-image-gallery__card--active .accordion-image-gallery__title {
  font-size: var(--pixso-fs-16);
  line-height: var(--pixso-lh-16);
}

.accordion-image-gallery__card:not(.accordion-image-gallery__card--active) .accordion-image-gallery__card-copy {
  gap: var(--accordion-collapsed-copy-gap);
}

.accordion-image-gallery__card--active .accordion-image-gallery__card-copy {
  gap: var(--accordion-copy-gap);
}

.accordion-image-gallery__card:not(.accordion-image-gallery__card--active) .accordion-image-gallery__initial {
  color: var(--pixso-color-muted);
}

.accordion-image-gallery__image-enter-active,
.accordion-image-gallery__image-leave-active {
  transition: opacity var(--accordion-card-duration) var(--accordion-ease), transform var(--accordion-card-duration) var(--accordion-ease);
}

.accordion-image-gallery__image-enter-from,
.accordion-image-gallery__image-leave-to {
  opacity: 0;
  transform: scale(1.05);
}

@media (max-width: 768px) {
  .accordion-image-gallery {
    width: 100%;
    max-width: none;
    grid-template-columns: 1fr;
    padding: 0;
    --accordion-card-collapsed-height: auto;
    --accordion-card-expanded-height: auto;
  }

  .accordion-image-gallery__list {
    height: auto;
  }

  .accordion-image-gallery__card {
    height: auto;
  }

  .accordion-image-gallery {
    gap: var(--pixso-fs-16);
  }

  .accordion-image-gallery__trigger {
    padding: var(--pixso-fs-16) var(--pixso-fs-20);
  }

  .accordion-image-gallery__detail {
    padding: var(--pixso-gap-sm) 0 0;
  }

  .accordion-image-gallery__media {
    height: 360px;
    border-radius: calc(var(--pixso-radius-lg) - var(--pixso-gap-sm));
  }
}

@media (prefers-reduced-motion: reduce) {
  .accordion-image-gallery__list,
  .accordion-image-gallery__card,
  .accordion-image-gallery__card-copy,
  .accordion-image-gallery__initial,
  .accordion-image-gallery__title,
  .accordion-image-gallery__summary,
  .accordion-image-gallery__detail-wrap,
  .accordion-image-gallery__detail,
  .accordion-image-gallery__image-enter-active,
  .accordion-image-gallery__image-leave-active {
    transition: none;
  }

  .accordion-image-gallery__image-enter-from,
  .accordion-image-gallery__image-leave-to {
    transform: none;
  }
}
</style>
